(() => {
  'use strict';
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const activateTab = (button) => {
    const tabs = button.closest('[data-gsp-tabs]');
    if (!tabs) return;
    const target = button.getAttribute('aria-controls');
    qsa('[data-gsp-tab]', tabs).forEach((tab) => tab.setAttribute('aria-selected', String(tab === button)));
    qsa('[data-gsp-panel]', tabs).forEach((panel) => { panel.hidden = panel.id !== target; });
  };

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-gsp-tab]');
    if (tab) activateTab(tab);
    const scrollButton = event.target.closest('[data-gsp-scroll-buy]');
    if (scrollButton) {
      const productButton = document.querySelector('[id^="ProductSubmitButton"], .product-form__submit');
      if (productButton) { event.preventDefault(); productButton.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' }); }
    }
  });

  const setupCounters = (root = document) => {
    qsa('[data-gsp-counter]:not([data-gsp-ready])', root).forEach((node) => {
      node.dataset.gspReady = 'true';
      const end = Number(node.dataset.gspCounter);
      if (!Number.isFinite(end) || matchMedia('(prefers-reduced-motion: reduce)').matches) { node.textContent = String(end); return; }
      const run = () => {
        const start = performance.now();
        const update = (now) => {
          const progress = Math.min((now - start) / 900, 1);
          node.textContent = String(Math.round(end * (1 - Math.pow(1 - progress, 3))));
          if (progress < 1) requestAnimationFrame(update);
        };
        requestAnimationFrame(update);
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) { observer.disconnect(); run(); } }, { threshold: .35 });
        observer.observe(node);
      } else run();
    });
  };

  class GoshkoQuiz extends HTMLElement {
    connectedCallback() {
      this.options = qsa('[data-gsp-quiz-option]', this);
      this.result = this.querySelector('[data-gsp-quiz-result]');
      this.options.forEach((option) => option.addEventListener('click', () => this.choose(option)));
    }
    choose(option) {
      const key = option.dataset.result;
      qsa('[data-gsp-result-card]', this).forEach((card) => { card.hidden = card.dataset.resultCard !== key; });
      if (this.result) { this.result.hidden = false; this.result.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
      this.options.forEach((item) => item.setAttribute('aria-pressed', String(item === option)));
    }
  }
  if (!customElements.get('goshko-product-quiz')) customElements.define('goshko-product-quiz', GoshkoQuiz);

  const setupSticky = (root = document) => {
    qsa('[data-gsp-sticky]:not([data-gsp-ready])', root).forEach((bar) => {
      bar.dataset.gspReady = 'true';
      const threshold = Number(bar.dataset.threshold || 500);
      const update = () => bar.classList.toggle('is-visible', window.scrollY > threshold);
      window.addEventListener('scroll', update, { passive: true }); update();
    });
  };

  const money = (minorUnits, currency) => {
    const amount = Number(minorUnits) / 100;
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(amount); }
    catch (error) { return amount.toFixed(2); }
  };

  const setupCountdowns = (root = document) => {
    qsa('[data-gsp-countdown]:not([data-gsp-ready])', root).forEach((timer) => {
      timer.dataset.gspReady = 'true';
      const end = new Date(timer.dataset.end).getTime();
      const values = qsa('[data-gsp-time]', timer);
      const expired = timer.parentElement.querySelector('[data-gsp-expired]');
      if (!Number.isFinite(end)) { timer.hidden = true; if (expired) expired.hidden = false; return; }
      const update = () => {
        const remaining = Math.max(0, end - Date.now());
        const parts = { days: Math.floor(remaining / 86400000), hours: Math.floor(remaining / 3600000) % 24, minutes: Math.floor(remaining / 60000) % 60, seconds: Math.floor(remaining / 1000) % 60 };
        values.forEach((value) => { value.textContent = String(parts[value.dataset.gspTime] ?? 0).padStart(2, '0'); });
        if (remaining <= 0) { clearInterval(interval); timer.hidden = true; if (expired) expired.hidden = false; }
      };
      const interval = setInterval(update, 1000); update();
    });
  };

  const setupSavings = (root = document) => {
    qsa('[data-gsp-savings]:not([data-gsp-ready])', root).forEach((calculator) => {
      calculator.dataset.gspReady = 'true';
      const current = calculator.querySelector('[data-gsp-current]');
      const replacement = calculator.querySelector('[data-gsp-replacement]');
      const monthly = calculator.querySelector('[data-gsp-monthly]');
      const annual = calculator.querySelector('[data-gsp-annual]');
      const percent = calculator.querySelector('[data-gsp-percent]');
      const currency = calculator.dataset.currency || 'USD';
      const update = () => {
        const oldValue = Math.max(0, Number(current.value) || 0);
        const newValue = Math.max(0, Number(replacement.value) || 0);
        const saved = Math.max(0, oldValue - newValue);
        monthly.textContent = money(saved * 100, currency); annual.textContent = money(saved * 1200, currency);
        percent.textContent = oldValue > 0 ? `${Math.round(saved / oldValue * 100)}%` : '0%';
      };
      [current, replacement].forEach((input) => input.addEventListener('input', update)); update();
    });
  };

  const setupCoverage = (root = document) => {
    qsa('[data-gsp-coverage]:not([data-gsp-ready])', root).forEach((calculator) => {
      calculator.dataset.gspReady = 'true';
      const area = calculator.querySelector('[data-gsp-area]');
      const coverage = calculator.querySelector('[data-gsp-coverage-input]');
      const waste = calculator.querySelector('[data-gsp-waste]');
      const packages = calculator.querySelector('[data-gsp-packages]');
      const total = calculator.querySelector('[data-gsp-total]');
      const update = () => {
        const needed = Math.max(0, Number(area.value) || 0) * (1 + Math.max(0, Number(waste.value) || 0) / 100);
        const perPackage = Math.max(.0001, Number(coverage.value) || 1);
        const count = Math.ceil(needed / perPackage);
        packages.textContent = String(count);
        total.textContent = money(count * Number(calculator.dataset.price || 0), calculator.dataset.currency || 'USD');
      };
      [area, coverage, waste].forEach((input) => input.addEventListener('input', update)); update();
    });
  };

  const setupFilters = (root = document) => {
    qsa('[data-gsp-filter-group]:not([data-gsp-ready])', root).forEach((group) => {
      group.dataset.gspReady = 'true';
      const buttons = qsa('[data-gsp-filter]', group);
      const items = qsa('[data-gsp-filter-item]', group);
      buttons.forEach((button) => button.addEventListener('click', () => {
        const filter = button.dataset.gspFilter;
        buttons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
        items.forEach((item) => { item.hidden = filter !== 'all' && !String(item.dataset.tags || '').split(' ').includes(filter); });
      }));
    });
  };

  const setupSectionNav = (root = document) => {
    qsa('[data-gsp-section-nav]:not([data-gsp-ready])', root).forEach((nav) => {
      nav.dataset.gspReady = 'true';
      const links = qsa('[data-gsp-nav-link]', nav);
      const targets = links.map((link) => document.getElementById(link.getAttribute('href').slice(1))).filter(Boolean);
      links.forEach((link) => link.addEventListener('click', (event) => {
        const target = document.getElementById(link.getAttribute('href').slice(1));
        if (target) { event.preventDefault(); target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); }
      }));
      if ('IntersectionObserver' in window && targets.length) {
        const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
          if (entry.isIntersecting) links.forEach((link) => link.classList.toggle('is-active', link.getAttribute('href') === `#${entry.target.id}`));
        }), { rootMargin: '-25% 0px -65% 0px' });
        targets.forEach((target) => observer.observe(target));
      }
    });
  };

  const setupCompare = (root = document) => {
    qsa('[data-gsp-compare]:not([data-gsp-ready])', root).forEach((compare) => {
      compare.dataset.gspReady = 'true';
      const choices = qsa('[data-gsp-compare-choice]', compare);
      const max = Number(compare.dataset.max || 3);
      const note = compare.querySelector('[data-gsp-compare-note]');
      const update = (changed) => {
        let selected = choices.filter((choice) => choice.checked);
        if (selected.length > max && changed) { changed.checked = false; selected = choices.filter((choice) => choice.checked); }
        qsa('[data-gsp-compare-column]', compare).forEach((column) => { column.hidden = !selected.some((choice) => choice.value === column.dataset.gspCompareColumn); });
        if (note) note.textContent = selected.length ? `${selected.length} selected` : note.dataset.empty;
      };
      choices.forEach((choice) => choice.addEventListener('change', () => update(choice))); update();
    });
  };

  const setupSizeTools = (root = document) => {
    qsa('[data-gsp-size]:not([data-gsp-ready])', root).forEach((tool) => {
      tool.dataset.gspReady = 'true';
      const input = tool.querySelector('[data-gsp-size-input]');
      const unit = tool.querySelector('[data-gsp-size-unit]');
      const empty = tool.querySelector('[data-gsp-size-empty]');
      const update = () => {
        const raw = Math.max(0, Number(input.value) || 0);
        const cm = unit.value === 'in' ? raw * 2.54 : raw;
        let matched = false;
        qsa('[data-gsp-size-card]', tool).forEach((card) => {
          const show = raw > 0 && cm >= Number(card.dataset.min) && cm <= Number(card.dataset.max);
          card.hidden = !show; matched ||= show;
        });
        if (empty) empty.hidden = raw <= 0 || matched;
      };
      [input, unit].forEach((control) => control.addEventListener('input', update)); update();
    });
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-gsp-dialog-open]');
    if (opener) document.getElementById(opener.dataset.gspDialogOpen)?.showModal();
    const closer = event.target.closest('[data-gsp-dialog-close]');
    if (closer) closer.closest('dialog')?.close();
  });

  const setup = (root = document) => {
    setupCounters(root); setupSticky(root); setupCountdowns(root); setupSavings(root); setupCoverage(root);
    setupFilters(root); setupSectionNav(root); setupCompare(root); setupSizeTools(root);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup()); else setup();
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();
